import React, { useState, useEffect } from "react";
import { Acompanante } from "../../types/acompanantes";
//import PersonaSearch from "../shared/PersonaSearchPicker";
import CrearPersonaModal from "../shared/CrearPersonaModal";
import PersonaSearchSQL from "../shared/PersonaSearch";
import { getPersonaVetadaByCedula } from "../../services/General/personaVetadaService";
import { PersonaSQL } from "../../types/PersonaSQL";
import Swal from "sweetalert2";


interface AcompanantesModalProps {
    acompanantes: Acompanante[];
    onChange: (acompanantes: Acompanante[]) => void;
    maxAcompanantes?: number;
}

const AcompanantesModal: React.FC<AcompanantesModalProps> = ({
    acompanantes,
    onChange,
    maxAcompanantes = 5,
}) => {
    const [localAcompanantes, setLocalAcompanantes] = useState<Acompanante[]>(acompanantes);
    const [loadingCedula, setLoadingCedula] = useState<string | null>(null);
    const [searchResetKey, setSearchResetKey] = useState<number>(0); // Para forzar reinicio del componente de búsqueda
    const [showCreatePersonaModal, setShowCreatePersonaModal] = useState(false);
    const [pendingSearchQuery, setPendingSearchQuery] = useState('');

    //Sincronizar con el prop
    useEffect(() => {
        setLocalAcompanantes(acompanantes);
    }, [acompanantes]);

    // 👇 Función para construir nombre completo desde PersonaSQL
    const construirNombreCompleto = (persona: PersonaSQL): string => {
        return `${persona.nombre} ${persona.apellido1} ${persona.apellido2}`.trim();
    };


    const handlePersonaSelect = async (persona: PersonaSQL | null) => {
        if (!persona) return; // Si es null, ignorar (limpieza)
        //Validar Limite
        if (localAcompanantes.length >= maxAcompanantes) {
            Swal.fire({
                title: 'Limite Alcanzado',
                text: `No se pueden agregar más de ${maxAcompanantes} acompañantes.`,
                icon: 'warning',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        //Verificar si ya existe
        const existe = localAcompanantes.some(a => a.cedula === persona.identificador);
        if (existe) {
            Swal.fire({
                title: 'Acompañante Existente',
                text: `El acompañante con cédula ${persona.identificador} ya ha sido agregado.`,
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }
        setLoadingCedula(persona.identificador);
        try {
            // Verificar Veto
            const personaVetada = await getPersonaVetadaByCedula(persona.identificador);
            if (personaVetada && personaVetada.is_active) {
                Swal.fire({
                    title: '¡ACOMPAÑANTE VETADO! ⛔',
                    html: `
                        <div class="text-start">
                            <p><strong>${construirNombreCompleto(persona)}</strong> (${persona.identificador})</p>
                            <div class="alert alert-danger mt-2">
                                <i class="bi bi-ban me-2"></i>
                                <strong>NO PUEDE SER ACOMPAÑANTE - PERSONA VETADA</strong>
                            </div>
                            <p class="mt-2"><strong>Motivo:</strong></p>
                            <p class="text-danger">${personaVetada.motivo}</p>
                        </div>
                    `,
                    icon: 'error',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#dc3545',
                    allowOutsideClick: false,
                    width: '500px'
                });
                setLoadingCedula(null);
                return;
            }
            //Si no está vetado, agregar
            const nuevoAcompanante: Acompanante = {
                cedula: persona.identificador,
                nombre: construirNombreCompleto(persona),
                email: '',
            };
            const nuevosAcompanantes = [...localAcompanantes, nuevoAcompanante];
            setLocalAcompanantes(nuevosAcompanantes);
            onChange(nuevosAcompanantes);
            // ✅ INCREMENTAR LA KEY PARA FORZAR REINICIO
            setSearchResetKey(prev => prev + 1);
        } catch (error) {
            console.error("Error al verificar persona vetada:", error);
            Swal.fire({
                title: 'Error al verificar persona vetada',
                text: 'No se pudo verificar si la persona está vetada.',
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
        } finally {
            setLoadingCedula(null);
        }
    };

    // Manejador para cuando no se encuentra la persona
    const handlePersonaNotFound = (query: string) => {
        setPendingSearchQuery(query); //Guarda el texto que uso el usuario
        setShowCreatePersonaModal(true); //Abre el modal
    };
    // Manejador para cuando se crea una nueva persona
    const handlePersonaCreada = (nuevaPersona: PersonaSQL) => {
        // La misma validación que en handlePersonaSelect
        if (localAcompanantes.length >= maxAcompanantes) {
            Swal.fire({
                title: 'Límite Alcanzado',
                text: `No se pueden agregar más de ${maxAcompanantes} acompañantes.`,
                icon: 'warning',
                confirmButtonText: 'Aceptar'
            });
            setShowCreatePersonaModal(false);
            return;
        }

        const existe = localAcompanantes.some(a => a.cedula === nuevaPersona.identificador);
        if (existe) {
            Swal.fire({
                title: 'Acompañante Existente',
                text: `El acompañante con cédula ${nuevaPersona.identificador} ya ha sido agregado.`,
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            setShowCreatePersonaModal(false);
            return;
        }

        // Agregar la nueva persona como acompañante
        const nuevoAcompanante: Acompanante = {
            cedula: nuevaPersona.identificador,
            nombre: construirNombreCompleto(nuevaPersona),
            email: '',
        };

        const nuevosAcompanantes = [...localAcompanantes, nuevoAcompanante];
        setLocalAcompanantes(nuevosAcompanantes);
        onChange(nuevosAcompanantes);

        // Resetear buscador y cerrar modal
        setSearchResetKey(prev => prev + 1);
        setShowCreatePersonaModal(false);

        Swal.fire({
            title: '¡Acompañante creado!',
            text: `${construirNombreCompleto(nuevaPersona)} ha sido agregado como acompañante`,
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
        });
    };


    // ✅ Función para limpiar manualmente
    const handleClearSearch = () => {
        setSearchResetKey(prev => prev + 1);
    };


    const handleRemoveAcompanante = (index: number) => {
        Swal.fire({
            title: '¿Eliminar Acompañante?',
            text: `¿Está seguro de eliminar a ${localAcompanantes[index].nombre}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
        }).then((result) => {
            if (result.isConfirmed) {
                const nuevosAcompanantes = localAcompanantes.filter((_, i) => i !== index);
                setLocalAcompanantes(nuevosAcompanantes);
                onChange(nuevosAcompanantes);
            }
        });
    };

    const handleEmailChange = (index: number, newEmail: string) => {
        const nuevosAcompanantes = [...localAcompanantes];
        nuevosAcompanantes[index].email = newEmail;
        setLocalAcompanantes(nuevosAcompanantes);
        onChange(nuevosAcompanantes);
    };

    const handleNombreChange = (index: number, newNombre: string) => {
        const nuevosAcompanantes = [...localAcompanantes];
        nuevosAcompanantes[index].nombre = newNombre;
        setLocalAcompanantes(nuevosAcompanantes);
        onChange(nuevosAcompanantes);
    };

    const validateEmail = (email: string) => {
        if (email.trim() === '' || !email) return true; // Permitir email vacío
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    };

    return (
        <div className='mb-4'>
            <div className="mb-3">
                <label htmlFor="" className="form-label small fw-bold">
                    <i className="bi bi-people me-2"></i>
                    Acompañantes (Maximo {maxAcompanantes})
                    <small className="text-muted ms-2">
                        ({localAcompanantes.length}/{maxAcompanantes})
                    </small>
                </label>
                {/* Componente de búsqueda de personas */}
                <div className="mb-3">
                    <div className="d-flex align-items-stretch">
                        <div className="flex-grow-1">
                            {/* <PersonaSearch
                                onPersonaSelect={handlePersonaSelect}
                                value={searchValue}
                                key={`persona-search-${searchKey}`} // Usar la key para reiniciar el componente
                            /> */}
                            <PersonaSearchSQL
                                key={`persona-search-${searchResetKey}`} // 👈 Usar la key para reset
                                onPersonaSelect={handlePersonaSelect}
                                onPersonaNotFound={handlePersonaNotFound}
                                value='' //siempre vacío porque se resetea
                            />
                        </div>
                        {/* Botón para limpiar manualmente */}
                        <button
                            type="button"
                            className="btn btn-outline-secondary ms-2"
                            onClick={handleClearSearch}
                            title="Limpiar búsqueda"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    {loadingCedula && (
                        <div className="text-info small mt-1">
                            <i className="bi bi-hourglass-split me-1"></i>
                            Verificando estado de la persona...
                        </div>
                    )}
                </div>
                {/* Lista de acompañantes */}
                {localAcompanantes.length > 0 && (
                    <div className="mt-3">
                        <div className="table-responsive">
                            <table className="table table-sm table-hover align-middle" >
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "20%" }} className="small" >Cedula</th>
                                        <th style={{ width: "35%" }} className="small">Nombre</th>
                                        <th style={{ width: "35%" }} className="small">Email</th>
                                        <th style={{ width: "10%" }} className="text-center small">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {localAcompanantes.map((acompanante, index) => (
                                        <tr key={index}>
                                            <td>
                                                <code className="bg-light px-2 py-1 rounded">{acompanante.cedula}</code>
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={acompanante.nombre}
                                                    onChange={(e) => handleNombreChange(index, e.target.value)}
                                                    placeholder="Nombre Completo"
                                                    required
                                                />
                                            </td>
                                            <td>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        type="email"
                                                        className={`form-control form-control-sm ${acompanante.email && !validateEmail(acompanante.email) ? 'is-invalid' : ''}`}
                                                        value={acompanante.email}
                                                        onChange={(e) => handleEmailChange(index, e.target.value)}
                                                        placeholder="optional@email"
                                                    />
                                                    {acompanante.email && !validateEmail(acompanante.email) && (
                                                        <div className="invalid-feedback">
                                                            Email no válido
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => handleRemoveAcompanante(index)}
                                                    title="Eliminar acompañante"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Resumen */}
                        {/* <div className="mt-3 p-2 bg-light rounded">
                            <div className="row">
                                <div className="col">
                                    <small>
                                        <i className="bi bi-circle me-1"></i>
                                        Total: <strong>{localAcompanantes.length}</strong> acompañantes
                                    </small>
                                </div>
                            </div>
                        </div> */}
                    </div>
                )}
                {/* Mensaje cuando hay acompañantes */}
                {localAcompanantes.length === 0 && (
                    <div className="alert alert-info mt-3">
                        <i className="bi bi-info-circle me-2"></i>
                        No hay acompañantes agregados. Use el buscador para agregar.
                    </div>
                )}
            </div>
             {/* Modal para crear nueva persona */}
            <CrearPersonaModal
                show={showCreatePersonaModal}
                onHide={() => setShowCreatePersonaModal(false)}
                searchQuery={pendingSearchQuery}
                onPersonaCreada={handlePersonaCreada}
            />
        </div>
    );

};

export default AcompanantesModal;